import React, { useState, Fragment } from 'react';
import T from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import SimpleFormat from '@16g/react-simple-format';

import skeletons from './Skeletons';
import PageModal from '../../../../../components/Modals/PageModal';
import { format, today, differenceInDays, isAfter, isSameOrBefore } from '../../../../../utils/date';

const InsightsVersions = ({ details, settings }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const about_data = get(details, 'app_about.about_data');
  const versions = get(details, 'app_about.versions');
  if (settings.pending || isEmpty(about_data) || isEmpty(about_data)) {
    return skeletons.detailsAppDetails;
  }

  const formattedReleasedDate = !isEmpty(about_data)
    && (about_data.released_on_dev_date_formatted || about_data.released_date_formatted);
  const formattedReleasedOnDate = !isEmpty(about_data) && format(about_data.released_on);
  const averageReleaseInterval = !isEmpty(about_data) && about_data.avg_release_interval;
  const releasedSubtitle = () => {
    if (isEmpty(versions)) return 'No version updates.';
    if (isEmpty(averageReleaseInterval) || (!isEmpty(versions) && versions.length === 1)) {
      return `Last version released ${differenceInDays(today(), versions[0].release_date)} days ago.`;
    }

    return `Releasing updates every ${averageReleaseInterval} on average. Last update ${differenceInDays(today(), versions[0].release_date)} days ago.`;
  };

  const header = (
    <div className="insights-details-block__head insights-details-block__versions">
      <div className="h4">Version History</div>
      <div className="insights-details-block__versions-subtitle">{releasedSubtitle()}</div>
    </div>
  );

  const renderMainContent = data => (
    <div className="insights-details-block__versions-truncate">
      <div className="insights-details-block__versions-list">
        {data.map(({
          description,
          version,
          release_date,
        }, i) => {
          const isEqualOrBeforeDates = isSameOrBefore(formattedReleasedOnDate, release_date);
          const isAfterNextDates = data[i + 1]
            && isAfter(formattedReleasedOnDate, data[i + 1].release_date);
          const isBeforeLastDates = data[data.length - 1]
            && isSameOrBefore(formattedReleasedOnDate, data[data.length - 1].release_date);
          const versionItem = (
            <div key={version} className="insights-details-block__versions-item">
              <div className="insights-details-block__versions-date">
                <span>{format(release_date)}</span>
              </div>
              <div className="insights-details-block__versions-version">
                <span>Version {version}</span>
              </div>
              <div className="insights-details-block__versions-description">
                <SimpleFormat text={description} />
              </div>
            </div>
          );
          const launchItem = (
            <div className="insights-details-block__versions-item launched-item">
              <div className="insights-details-block__versions-meta">
                <span>Launched </span>
                <span>{formattedReleasedDate}</span>
              </div>
            </div>
          );

          if (isEqualOrBeforeDates && isAfterNextDates) {
            return (
              <Fragment key={`${release_date + version} Launched`}>
                {launchItem}
                {versionItem}
              </Fragment>
            );
          }
          if (i === data.length - 1 && isBeforeLastDates) {
            return (
              <Fragment key={`${release_date + version} Launched last`}>
                {launchItem}
              </Fragment>
            );
          }
          return (
            <Fragment key={release_date + version}>
              {versionItem}
            </Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="insights-details-block__versions-box">
      {versions && renderMainContent(versions.slice(0, 3))}
      <div
        className="insights-details-block__versions-view-more"
        onClick={() => setModalOpen(true)}
      >
        View full version history
      </div>
      <PageModal
        open={modalOpen}
        header={header}
        main={renderMainContent(versions)}
        maxModalWidth={550}
        shouldCloseOnOverlayClick
        className="versions-modal"
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

InsightsVersions.propTypes = {
  details: T.object,
  settings: T.object,
};

export default InsightsVersions;
